<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-backdrop-reset" @close="close">
      <Backdrop />

      <div class="fixed inset-0 z-backdrop-content overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-md bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  class="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  @click="close"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <ExclamationTriangleIcon
                    class="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-lg font-medium leading-6 text-gray-900"
                    >{{
                      title ?? t("admin.settings.confirmation.title")
                    }}</DialogTitle
                  >
                  <div class="mt-2">
                    <p
                      class="text-sm text-gray-500"
                      v-if="slots.default && slots.default !== ''"
                    >
                      <slot />
                    </p>
                    <p class="text-sm text-gray-500" v-else>
                      {{ t("admin.settings.confirmation.message") }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  :disabled="props.disabled"
                  type="button"
                  class="inline-flex w-full justify-center rounded-sm border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  @click="confirm"
                >
                  {{
                    confirmationLabel ??
                    t("admin.settings.confirmation.confirm")
                  }}
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-sm border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  @click="close"
                >
                  {{ cancelLabel ?? t("admin.settings.confirmation.cancel") }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { defineProps, defineEmits, useSlots } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";
import Backdrop from "@/Tailwind/Components/Backdrop.vue";

const emit = defineEmits(["confirm", "close"]);
const props = defineProps({
  open: Boolean,
  confirmationLabel: String,
  cancelLabel: String,
  title: String,
  disabled: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();

const { t } = useI18n();

const confirm = () => emit("confirm");
const close = () => emit("close");
</script>
